/**
 * User detail, rebuilt from scratch on the same pieces as `pages/orders/Detail.tsx`:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero: the account's avatar tile, name, handle and actions (`PageHero`);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the four figure cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/explore-flight/listing-filters/flight-filter.tsx
 *   rizzui's `Tab` list and panels;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks in the profile card (`InfoBlock`);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/social-media/dashboard/recent-activities.tsx
 *   the activity feed rows;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/columns.tsx
 *   the role badge and status vocabulary the list page already uses.
 *
 * Rules this screen keeps:
 * - Role and sessions are the Super Admin's (`company.user.administer`); status is `user.status`.
 *   Every control is offered only to the permission that the API will accept it from, and never on
 *   the caller's own account (FR-040: the API answers each with 409 there).
 * - `locked` is Identity's computed flag (FR-001a) — shown beside the status, never folded into it.
 * - The activity is Identity's own audit trail filtered to this account (`GET /api/identity/audit`,
 *   `identity.audit.read`). It is read only when the caller holds that permission — a refused read
 *   would itself be an audit entry — and it is not awaited, so the account renders first and a
 *   failed read says so in its own card instead of taking the page down (constitution VI).
 */
import { useState, type ReactNode } from "react";
import {
  PiArrowRightBold as ArrowRight,
  PiBuildingsDuotone as CompanyIcon,
  PiCheckCircleDuotone as ActiveIcon,
  PiClockCounterClockwiseDuotone as HistoryIcon,
  PiDesktopDuotone as SessionIcon,
  PiDotsThreeOutlineVerticalFill as MoreIcon,
  PiEnvelopeSimpleDuotone as MailIcon,
  PiIdentificationBadgeDuotone as RoleIcon,
  PiInfoDuotone as InfoIcon,
  PiKeyDuotone as KeyIcon,
  PiLockKeyDuotone as LockIcon,
  PiLockKeyOpenDuotone as UnlockIcon,
  PiPhoneDuotone as PhoneIcon,
  PiProhibitDuotone as DeactivateIcon,
  PiShieldCheckDuotone as ShieldIcon,
  PiShieldWarningDuotone as ShieldWarningIcon,
  PiSignInDuotone as SignInIcon,
  PiSignOutDuotone as SignOutIcon,
  PiUserCircleDuotone as UserIcon,
  PiUserGearDuotone as UserGearIcon,
  PiUserPlusDuotone as InvitedIcon,
} from "react-icons/pi";
import { Alert, Badge, Button, Dropdown, Tab, Text } from "rizzui";
import toast from "react-hot-toast";
import { Link, useLoaderData, useOutletContext, useRevalidator, type LoaderFunctionArgs } from "react-router-dom";
import { EmptyState, ErrorSurface, MetricCard, WidgetCard, cn } from "@steelyard/theme";
import { useConfirm } from "../../components/confirm-modal";
import { ChangeRoleForm } from "../../components/users/change-role-form";
import { TileBoundary } from "../../components/dashboard/TileBoundary";
import { CardTitle, CopyButton, InfoBlock, PageHero, StatusPill, iconMenuTriggerClass } from "../../components/detail-parts";
import {
  changeUserRole,
  deactivateUser,
  noCompanyLabel,
  userByKey,
  reactivateUser,
  revokeUserSessions,
  unlockUser,
  userStatusLabel,
  type UserSummary,
} from "../../lib/services/identity";
import { eventTypeLabel, listAuditLog, type AuditLogEntry } from "../../lib/services/audit";
import { getPermissionState, listGrantableRoles } from "../../lib/identity";
import { ApiError } from "../../lib/api";
import type { PagedResult } from "../../components/table/data-table";
import { can } from "../../lib/permissions";
import { formatDate, formatMoment, roleName } from "../../lib/format";
import type { AccountSummary, PermissionState } from "../../types";

const ACTIVITY_PAGE_SIZE = 15;

export async function usersDetailLoader({ params, request }: LoaderFunctionArgs) {
  const [user, grantableRoles, permissions] = await Promise.all([
    userByKey(request, params.userNumber!),
    listGrantableRoles(),
    getPermissionState(),
  ]);
  // Deliberately not awaited — see the file header. `TileBoundary` resolves it on its own.
  const activity = user && can(permissions, "identity.audit.read")
    ? listAuditLog({ subjectAccountId: user.id, page: 1, pageSize: ACTIVITY_PAGE_SIZE })
    : null;
  // Identity sends the company's name beside its reference; the reference itself is never shown.
  const companyName = user?.companyRef ? (user.companyName ?? "Unknown customer") : null;
  return { user, grantableRoles, activity, companyName };
}

function problemMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.detail ?? err.title ?? fallback) : fallback;
}

/** Two letters for the avatar tile, from whatever name the account has. */
function initials(user: UserSummary): string {
  const words = (user.displayName ?? user.email ?? "?").split(/[\s@._-]+/).filter(Boolean);
  const [first = "?", second] = words;
  return (second ? first.charAt(0) + second.charAt(0) : first.slice(0, 2)).toUpperCase();
}

/** The hero tile's colour follows the account's state — the same meaning its status badge carries. */
function stateTile(user: UserSummary): string {
  if (user.locked) return "bg-red";
  switch (user.status) {
    case "active":
      return "bg-green";
    case "pending":
      return "bg-blue";
    default:
      return "bg-gray-500";
  }
}

export default function UsersDetail() {
  const { user, grantableRoles, activity, companyName } = useLoaderData() as Awaited<ReturnType<typeof usersDetailLoader>>;
  const { account, permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  // Role and sessions are the Super Admin's (`company.user.administer`); status is `user.status`.
  const mayAdminister = can(permissionState, "company.user.administer");
  const mayChangeStatus = can(permissionState, "user.status");
  const mayReadAudit = can(permissionState, "identity.audit.read");
  const revalidator = useRevalidator();
  const confirm = useConfirm();
  const [roleError, setRoleError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(0);

  if (!user) {
    return <ErrorSurface error={{ title: "Not found", status: 404 }} action="load this user" />;
  }
  // Re-bound so the handlers below (nested functions, not narrowed by the guard above) read a type
  // TypeScript knows is non-null, without an `as` cast.
  const current: UserSummary = user;
  const id = current.id;
  const name = current.displayName ?? current.email ?? "Unknown user";

  // FR-040: an administrator cannot act on their own account — the API answers every one of these
  // with 409 for it, so the controls are not offered rather than offered to fail.
  const isSelf = account.id === current.id;

  // What this caller may do to this account, decided once so the hero, the menu and the Access tab agree.
  const statusAction: "deactivate" | "reactivate" | "activate" | null = isSelf || !mayChangeStatus
    ? null
    : current.status === "active"
      ? "deactivate"
      : current.status === "deactivated"
        ? "reactivate"
        : current.status === "pending" && mayAdminister
          ? "activate"
          : null;
  const mayUnlock = !isSelf && mayChangeStatus && current.locked;
  const mayRevoke = !isSelf && mayAdminister;
  const mayChangeRole = !isSelf && mayAdminister;

  async function handleChangeRole(formData: FormData) {
    setRoleError(null);
    try {
      await changeUserRole(id, String(formData.get("role") ?? ""));
      toast.success("Role changed. Every session this account held has been signed out.");
      revalidator.revalidate();
    } catch (err) {
      setRoleError(err);
      toast.error(problemMessage(err, "Could not change the role."));
    }
  }

  function handleDeactivate() {
    confirm({
      title: `Deactivate ${name}`,
      consequence: "All of its sessions are signed out immediately.",
      confirmLabel: "Deactivate",
      onConfirm: () => void run(() => deactivateUser(id), "Account deactivated", "Could not deactivate this account."),
    });
  }

  function handleRevokeSessions() {
    confirm({
      title: "Sign this account out everywhere",
      consequence: "It stays active — this only ends its current sessions.",
      confirmLabel: "Sign out everywhere",
      onConfirm: () =>
        void run(() => revokeUserSessions(id), "Every session for this account has been revoked.", "Could not revoke this account's sessions.", false),
    });
  }

  async function run(act: () => Promise<void>, success: string, failure: string, refresh = true) {
    setBusy(true);
    try {
      await act();
      toast.success(success);
      if (refresh) revalidator.revalidate();
    } catch (err) {
      toast.error(problemMessage(err, failure));
    } finally {
      setBusy(false);
    }
  }

  const reactivate = () => run(() => reactivateUser(id), "Account reactivated", "Could not reactivate this account.");
  const activate = () => run(() => reactivateUser(id), "Account activated", "Could not activate this account.");
  const unlock = () => run(() => unlockUser(id), "Account unlocked", "Could not unlock this account.");

  const primaryAction =
    statusAction === "deactivate" ? (
      <Button type="button" color="danger" variant="outline" disabled={busy} onClick={handleDeactivate}>
        <DeactivateIcon aria-hidden className="me-1.5 size-4" />
        Deactivate
      </Button>
    ) : statusAction === "reactivate" ? (
      <Button type="button" disabled={busy} onClick={reactivate}>
        <ActiveIcon aria-hidden className="me-1.5 size-4" />
        Reactivate
      </Button>
    ) : statusAction === "activate" ? (
      <Button type="button" disabled={busy} onClick={activate}>
        <ActiveIcon aria-hidden className="me-1.5 size-4" />
        Activate
      </Button>
    ) : null;

  const menu =
    mayUnlock || mayRevoke || mayReadAudit ? (
      <Dropdown placement="bottom-end">
        <Dropdown.Trigger aria-label={`More actions for ${name}`} disabled={busy} className={iconMenuTriggerClass}>
          <MoreIcon aria-hidden className="size-4" />
        </Dropdown.Trigger>
        <Dropdown.Menu className="w-64">
          {mayUnlock ? (
            <Dropdown.Item onClick={unlock} className="gap-2.5 text-sm">
              <UnlockIcon aria-hidden className="size-4 shrink-0 text-green-dark" />
              Unlock sign-in
            </Dropdown.Item>
          ) : null}
          {mayRevoke ? (
            <Dropdown.Item onClick={handleRevokeSessions} className="gap-2.5 text-sm">
              <SignOutIcon aria-hidden className="size-4 shrink-0 text-orange-dark" />
              Sign out everywhere
            </Dropdown.Item>
          ) : null}
          {mayReadAudit ? (
            <Dropdown.Item as="button" onClick={() => setTab(2)} className="gap-2.5 text-sm">
              <HistoryIcon aria-hidden className="size-4 shrink-0 text-secondary" />
              Show activity
            </Dropdown.Item>
          ) : null}
        </Dropdown.Menu>
      </Dropdown>
    ) : null;

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Users", href: "/users" }, { name }]}
        back={{ href: "/users", label: "All users" }}
        icon={<span className="text-xl font-bold tracking-wide">{initials(current)}</span>}
        iconClassName={stateTile(current)}
        title={name}
        copyValue={current.email ?? current.contactEmail ?? undefined}
        description={
          <>
            {current.email ?? current.contactEmail ?? "No email on file"}
            {(current.mobile ?? current.contactMobile) ? <> · {current.mobile ?? current.contactMobile}</> : null}
          </>
        }
        meta={
          <>
            <StatusPill status={userStatusLabel(current.status)} />
            <Badge variant="flat" color="secondary" rounded="pill" className="gap-1.5">
              <RoleIcon aria-hidden className="size-4" />
              {roleName(current.role)}
            </Badge>
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <CompanyIcon aria-hidden className="size-4" />
              {current.companyRef ? "Customer account user" : noCompanyLabel(current)}
            </Badge>
            {current.locked ? (
              <Badge variant="flat" color="danger" rounded="pill" className="gap-1.5">
                <ShieldWarningIcon aria-hidden className="size-4" />
                Locked{current.lockedUntil ? ` until ${formatDate(current.lockedUntil)}` : ""}
              </Badge>
            ) : null}
            {isSelf ? (
              <Badge variant="flat" color="info" rounded="pill">
                This is you
              </Badge>
            ) : null}
          </>
        }
        actions={
          primaryAction || menu ? (
            <>
              {primaryAction}
              {menu}
            </>
          ) : null
        }
      />

      {isSelf ? (
        <Alert color="info" variant="flat" bar icon={<InfoIcon aria-hidden className="size-5" />}>
          <Text className="font-semibold">This is your own account</Text>
          <Text className="mt-0.5 text-sm">
            Role, deactivation and session revocation are refused on the caller&apos;s own account. Someone else with the
            authority does it.
          </Text>
        </Alert>
      ) : null}

      {current.locked ? (
        <Alert color="danger" variant="flat" bar icon={<LockIcon aria-hidden className="size-5" />}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Text className="font-semibold text-red-dark">Sign-in is locked</Text>
              <Text className="mt-0.5 text-sm">
                Too many failed sign-in attempts.
                {current.lockedUntil ? ` It unlocks by itself on ${formatDate(current.lockedUntil)}.` : ""}
              </Text>
            </div>
            {mayUnlock ? (
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={unlock} className="border-red-dark text-red-dark hover:bg-red-dark hover:text-white">
                <UnlockIcon aria-hidden className="me-1.5 size-4" />
                Unlock now
              </Button>
            ) : null}
          </div>
        </Alert>
      ) : null}

      {/* ── Figures ──────────────────────────────────────────────────────────────────────── */}
      <div className="grid gap-4 @lg:grid-cols-2 @6xl:grid-cols-4">
        <MetricCard
          title="Role"
          metric={roleName(current.role)}
          icon={<RoleIcon aria-hidden className="size-6" />}
          iconClassName="bg-secondary text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Permissions come from the role matrix</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Account status"
          metric={sentence(userStatusLabel(current.status))}
          icon={<ShieldIcon aria-hidden className="size-6" />}
          iconClassName={cn(current.status === "active" ? "bg-green" : current.status === "pending" ? "bg-blue" : "bg-gray-500", "text-white shadow-sm")}
          info={<Text className="mt-0.5 text-xs text-gray-500">{statusMeaning(current.status)}</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Sign-in"
          metric={current.locked ? "Locked" : "Not locked"}
          icon={current.locked ? <LockIcon aria-hidden className="size-6" /> : <SignInIcon aria-hidden className="size-6" />}
          iconClassName={cn(current.locked ? "bg-red" : "bg-blue", "text-white shadow-sm")}
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              {current.locked ? (current.lockedUntil ? `Until ${formatDate(current.lockedUntil)}` : "Until an administrator unlocks it") : "No lockout on record"}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Organisation"
          metric={current.companyRef ? "Customer account" : current.registeredAsCompany === null ? "Infra Junction staff" : "Customer sign-up"}
          icon={<CompanyIcon aria-hidden className="size-6" />}
          iconClassName="bg-orange text-white shadow-sm"
          info={
            <Text className="mt-0.5 truncate text-xs text-gray-500">
              {current.companyRef
                ? companyName
                : current.registeredAsCompany === null
                  ? "No company — back-office account"
                  : noCompanyLabel(current)}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
      </div>

      {/* ── Work area: tabs on the left, the account's state and security on the right ───── */}
      <div className="flex flex-col gap-6 @5xl:grid @5xl:grid-cols-12 @5xl:items-start">
        <div className="@5xl:col-span-8">
          <WidgetCard rounded="xl" className="p-0 @lg:p-0 lg:p-0" headerClassName="hidden">
            <Tab selectedIndex={tab} onChange={setTab} highlightClassName="bg-secondary">
              <Tab.List className="gap-6 border-b border-muted px-5 pt-3 @3xl:px-7">
                <Tab.ListItem className="gap-2 pb-3 font-medium before:bg-secondary hover:text-secondary-dark" activeClassName="text-secondary-dark">
                  <UserIcon aria-hidden className="size-4" />
                  Overview
                </Tab.ListItem>
                <Tab.ListItem className="gap-2 pb-3 font-medium before:bg-secondary hover:text-secondary-dark" activeClassName="text-secondary-dark">
                  <UserGearIcon aria-hidden className="size-4" />
                  Role &amp; access
                </Tab.ListItem>
                <Tab.ListItem className="gap-2 pb-3 font-medium before:bg-secondary hover:text-secondary-dark" activeClassName="text-secondary-dark">
                  <HistoryIcon aria-hidden className="size-4" />
                  Activity
                </Tab.ListItem>
              </Tab.List>
              <Tab.Panels>
                <Tab.Panel className="px-5 py-6 @3xl:px-7">
                  <Overview user={current} companyName={companyName} />
                </Tab.Panel>
                <Tab.Panel className="px-5 py-6 @3xl:px-7">
                  <Access
                    user={current}
                    isSelf={isSelf}
                    mayChangeRole={mayChangeRole}
                    statusAction={statusAction}
                    mayUnlock={mayUnlock}
                    mayRevoke={mayRevoke}
                    busy={busy}
                    grantableRoles={grantableRoles}
                    roleError={roleError}
                    onChangeRole={handleChangeRole}
                    onDeactivate={handleDeactivate}
                    onReactivate={reactivate}
                    onActivate={activate}
                    onUnlock={unlock}
                    onRevoke={handleRevokeSessions}
                  />
                </Tab.Panel>
                <Tab.Panel className="px-5 py-6 @3xl:px-7">
                  <Activity activity={activity} subjectId={id} mayReadAudit={mayReadAudit} />
                </Tab.Panel>
              </Tab.Panels>
            </Tab>
          </WidgetCard>
        </div>

        <div className="flex flex-col gap-6 @5xl:col-span-4">
          <AccountState user={current} />
          <SecurityNotes />
        </div>
      </div>
    </div>
  );
}

function sentence(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function statusMeaning(status: string): string {
  switch (status) {
    case "active":
      return "Can sign in and work";
    case "pending":
      return "Invited — has not set a password yet";
    case "deactivated":
      return "Cannot sign in; reversible";
    case "erased":
      return "Personal data erased; permanent";
    default:
      return "As reported by Identity";
  }
}

// ── Overview tab ─────────────────────────────────────────────────────────────────────────────

function Overview({ user, companyName }: { user: UserSummary; companyName?: string | null }) {
  // The sign-in email, else the one given in the profile (a mobile-registered customer's).
  const email = user.email ?? user.contactEmail ?? null;
  // Likewise the sign-in mobile, else the one given in the profile (an email-registered account's).
  const mobile = user.mobile ?? user.contactMobile ?? null;
  return (
    <div className="grid gap-6 @2xl:grid-cols-2">
      <InfoBlock icon={<UserIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Display name">
        <Text className="font-medium text-gray-900">{user.displayName ?? "Not set"}</Text>
      </InfoBlock>
      <InfoBlock
        icon={<MailIcon aria-hidden className="size-4" />}
        tile="bg-blue-lighter text-blue-dark"
        label="Email"
        action={email ? <CopyButton value={email} label="Copy email" /> : undefined}
      >
        <Text className="break-all font-medium text-gray-900">{email ?? "None on file"}</Text>
      </InfoBlock>
      <InfoBlock
        icon={<PhoneIcon aria-hidden className="size-4" />}
        tile="bg-green-lighter text-green-dark"
        label="Mobile"
        action={mobile ? <CopyButton value={mobile} label="Copy mobile" /> : undefined}
      >
        <Text className="font-medium text-gray-900">{mobile ?? "None on file"}</Text>
      </InfoBlock>
      <InfoBlock icon={<RoleIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Role">
        <Text className="font-medium text-gray-900">{roleName(user.role)}</Text>
      </InfoBlock>
      <InfoBlock
        icon={<CompanyIcon aria-hidden className="size-4" />}
        tile="bg-orange-lighter text-orange-dark"
        label="Company"
      >
        <Text className="break-all font-medium text-gray-900">{user.companyRef ? companyName : user.registeredAsCompany === null ? "Infra Junction staff — no company" : noCompanyLabel(user)}</Text>
      </InfoBlock>
    </div>
  );
}

// ── Role & access tab ────────────────────────────────────────────────────────────────────────

function Access({
  user,
  isSelf,
  mayChangeRole,
  statusAction,
  mayUnlock,
  mayRevoke,
  busy,
  grantableRoles,
  roleError,
  onChangeRole,
  onDeactivate,
  onReactivate,
  onActivate,
  onUnlock,
  onRevoke,
}: {
  user: UserSummary;
  isSelf: boolean;
  mayChangeRole: boolean;
  statusAction: "deactivate" | "reactivate" | "activate" | null;
  mayUnlock: boolean;
  mayRevoke: boolean;
  busy: boolean;
  grantableRoles: string[];
  roleError: unknown;
  onChangeRole: (formData: FormData) => Promise<void>;
  onDeactivate: () => void;
  onReactivate: () => void;
  onActivate: () => void;
  onUnlock: () => void;
  onRevoke: () => void;
}) {
  const rows: ReactNode[] = [];

  if (statusAction === "deactivate") {
    rows.push(
      <AccessRow
        key="status"
        icon={<DeactivateIcon aria-hidden className="size-5" />}
        tile="bg-red-lighter text-red-dark"
        title="Deactivate this account"
        text="It can no longer sign in, and every session it holds ends immediately. Reversible."
        action={
          <Button type="button" color="danger" variant="outline" size="sm" disabled={busy} onClick={onDeactivate}>
            Deactivate
          </Button>
        }
      />,
    );
  } else if (statusAction === "reactivate") {
    rows.push(
      <AccessRow
        key="status"
        icon={<ActiveIcon aria-hidden className="size-5" />}
        tile="bg-green-lighter text-green-dark"
        title="Reactivate this account"
        text="It can sign in again. No old session is restored — revocation does not un-happen."
        action={
          <Button type="button" size="sm" disabled={busy} onClick={onReactivate}>
            Reactivate
          </Button>
        }
      />,
    );
  } else if (statusAction === "activate") {
    rows.push(
      <AccessRow
        key="status"
        icon={<InvitedIcon aria-hidden className="size-5" />}
        tile="bg-blue-lighter text-blue-dark"
        title="Activate this invited account"
        text="Activates it without waiting for the invitation to be accepted."
        action={
          <Button type="button" size="sm" disabled={busy} onClick={onActivate}>
            Activate
          </Button>
        }
      />,
    );
  }

  if (mayUnlock) {
    rows.push(
      <AccessRow
        key="unlock"
        icon={<UnlockIcon aria-hidden className="size-5" />}
        tile="bg-green-lighter text-green-dark"
        title="Unlock sign-in"
        text={`Clears the lockout now${user.lockedUntil ? ` instead of on ${formatDate(user.lockedUntil)}` : ""}.`}
        action={
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onUnlock}>
            Unlock
          </Button>
        }
      />,
    );
  }

  if (mayRevoke) {
    rows.push(
      <AccessRow
        key="revoke"
        icon={<SessionIcon aria-hidden className="size-5" />}
        tile="bg-orange-lighter text-orange-dark"
        title="Sign out everywhere"
        text="Ends every current session. The account stays active; takes effect within one access-token lifetime."
        action={
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onRevoke}>
            Revoke every session
          </Button>
        }
      />,
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section>
        <CardTitle icon={<RoleIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
          <span>
            <Text className="font-semibold text-gray-900">Role</Text>
            <Text className="text-sm text-gray-500">Validated against the role matrix on save.</Text>
          </span>
        </CardTitle>
        <div className="mt-4 rounded-xl border border-muted p-4 @3xl:p-5">
          {mayChangeRole ? (
            <ChangeRoleForm currentRole={user.role} grantableRoles={grantableRoles} onSubmit={onChangeRole} error={roleError} />
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Badge variant="flat" color="secondary" rounded="pill" className="gap-1.5">
                <RoleIcon aria-hidden className="size-4" />
                {roleName(user.role)}
              </Badge>
              <Text className="text-sm text-gray-500">
                {isSelf ? "Your own role is changed by someone else." : "Changing a role needs Super Admin authority."}
              </Text>
            </div>
          )}
        </div>
      </section>

      <section>
        <CardTitle icon={<KeyIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
          <span>
            <Text className="font-semibold text-gray-900">Access</Text>
            <Text className="text-sm text-gray-500">Deactivating and changing the role both sign this account out everywhere.</Text>
          </span>
        </CardTitle>
        <div className="mt-4">
          {rows.length > 0 ? (
            <div className="divide-y divide-muted rounded-xl border border-muted">{rows}</div>
          ) : (
            <EmptyState title="Nothing to change here">
              {user.status === "erased"
                ? "Erased users hold no identity key and cannot be reactivated."
                : isSelf
                  ? "These controls are refused on your own account."
                  : "Your role holds no access control for this account."}
            </EmptyState>
          )}
        </div>
      </section>
    </div>
  );
}

function AccessRow({ icon, tile, title, text, action }: { icon: ReactNode; tile: string; title: string; text: string; action: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 p-4 @2xl:flex-row @2xl:items-center @2xl:justify-between @3xl:px-5">
      <div className="flex items-start gap-3">
        <span className={cn("flex size-10 shrink-0 items-center justify-center rounded-xl", tile)}>{icon}</span>
        <div className="min-w-0">
          <Text className="font-semibold text-gray-900">{title}</Text>
          <Text className="mt-0.5 text-sm text-gray-500">{text}</Text>
        </div>
      </div>
      <div className="shrink-0 @2xl:ps-4">{action}</div>
    </div>
  );
}

// ── Activity tab ─────────────────────────────────────────────────────────────────────────────

function Activity({
  activity,
  subjectId,
  mayReadAudit,
}: {
  activity: Promise<PagedResult<AuditLogEntry>> | null;
  subjectId: string;
  mayReadAudit: boolean;
}) {
  if (!mayReadAudit || !activity) {
    return (
      <EmptyState title="Activity is not available to your role">
        This account&apos;s history lives in the identity audit trail, which needs the audit-read permission.
      </EmptyState>
    );
  }

  return (
    <TileBoundary
      resolve={activity}
      fallbackClassName="h-[320px]"
      errorFallback={(error) => <ErrorSurface error={error} action="load this account's activity" />}
    >
      {(page) =>
        page.items.length === 0 ? (
          <EmptyState title="No activity recorded">Sign-ins, role changes and status changes appear here as they happen.</EmptyState>
        ) : (
          <div className="flex flex-col">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <Text className="text-sm text-gray-500">
                Latest {page.items.length} of {page.totalCount} {page.totalCount === 1 ? "entry" : "entries"}
              </Text>
              <Link to={`/audit?subjectAccountId=${encodeURIComponent(subjectId)}`} className="inline-flex items-center gap-1 text-sm font-medium text-blue-dark hover:underline">
                Full history in the audit log
                <ArrowRight aria-hidden className="size-3.5" />
              </Link>
            </div>
            <ol className="flex flex-col">
              {page.items.map((entry, index) => (
                <ActivityRow key={entry.id} entry={entry} subjectId={subjectId} last={index === page.items.length - 1} />
              ))}
            </ol>
          </div>
        )
      }
    </TileBoundary>
  );
}

function activityIcon(eventType: string): ReactNode {
  if (eventType.startsWith("sign_in") || eventType.startsWith("otp")) return <SignInIcon aria-hidden className="size-4" />;
  if (eventType.startsWith("session")) return <SessionIcon aria-hidden className="size-4" />;
  if (eventType.startsWith("password")) return <KeyIcon aria-hidden className="size-4" />;
  if (eventType === "role.changed") return <RoleIcon aria-hidden className="size-4" />;
  if (eventType === "account.locked") return <LockIcon aria-hidden className="size-4" />;
  if (eventType === "account.unlocked") return <UnlockIcon aria-hidden className="size-4" />;
  if (eventType.startsWith("invitation") || eventType === "account.invited") return <InvitedIcon aria-hidden className="size-4" />;
  return <ShieldIcon aria-hidden className="size-4" />;
}

function ActivityRow({ entry, subjectId, last }: { entry: AuditLogEntry; subjectId: string; last: boolean }) {
  // The API's own outcome word; anything but a success is shown as one, never re-classified here.
  const succeeded = entry.outcome === "success";
  const byThisAccount = entry.actorAccountId === subjectId;

  return (
    <li className="group relative flex gap-4 pb-5 last:pb-0">
      {!last ? <span aria-hidden className="absolute bottom-0 start-[17px] top-10 w-0.5 bg-muted" /> : null}
      <span
        className={cn(
          "relative z-10 flex size-9 shrink-0 items-center justify-center rounded-full ring-4 ring-gray-0 dark:ring-gray-50",
          succeeded ? "bg-secondary-lighter text-secondary-dark" : "bg-red-lighter text-red-dark",
        )}
      >
        {activityIcon(entry.eventType)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1 rounded-lg px-3 py-1.5 transition-colors group-hover:bg-gray-50 dark:group-hover:bg-gray-100/40">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Text as="span" className="text-sm font-semibold text-gray-900">
              {eventTypeLabel(entry.eventType)}
            </Text>
            <Badge variant="flat" size="sm" rounded="pill" color={succeeded ? "success" : "danger"}>
              {sentence(entry.outcome.replace(/_/g, " "))}
            </Badge>
          </div>
          <Text as="span" className="text-xs text-gray-500">
            {formatMoment(entry.occurredAt)}
          </Text>
        </div>
        {entry.before || entry.after ? (
          <Text className="flex flex-wrap items-center gap-1.5 text-sm text-gray-700">
            {entry.before ? <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">{entry.before}</span> : null}
            {entry.before && entry.after ? <ArrowRight aria-hidden className="size-3 text-gray-400" /> : null}
            {entry.after ? <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">{entry.after}</span> : null}
          </Text>
        ) : null}
        <Text className="text-xs text-gray-500">
          {entry.actorAccountId === null ? "By the system" : byThisAccount ? "By this account" : "By another account"}
          {entry.client ? ` · ${entry.client}` : ""}
          {entry.sourceIp ? ` · ${entry.sourceIp}` : ""}
        </Text>
      </div>
    </li>
  );
}

// ── Side column ──────────────────────────────────────────────────────────────────────────────

function AccountState({ user }: { user: UserSummary }) {
  // The lifecycle Identity reports (`AccountStatus.cs`): invited → active ⇄ deactivated, and erased.
  const steps = [
    { key: "pending", label: "Invited", text: "Invitation sent" },
    { key: "active", label: "Active", text: "Can sign in" },
    { key: "deactivated", label: "Deactivated", text: "Sign-in refused" },
  ];

  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<ShieldIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
          Account state
        </CardTitle>
      }
      headerClassName="items-center"
    >
      <ul className="mt-5 flex flex-col gap-3">
        {steps.map((step) => {
          const now = user.status === step.key;
          return (
            <li
              key={step.key}
              className={cn(
                "flex items-center gap-3 rounded-lg border px-3 py-2.5",
                now ? "border-secondary bg-secondary-lighter/30 dark:bg-secondary-lighter/10" : "border-muted",
              )}
            >
              <span className={cn("size-2.5 shrink-0 rounded-full", now ? "bg-secondary" : "bg-gray-300")} />
              <div className="min-w-0 flex-1">
                <Text className={cn("text-sm", now ? "font-semibold text-gray-900" : "text-gray-600")}>{step.label}</Text>
                <Text className="text-xs text-gray-500">{step.text}</Text>
              </div>
              {now ? (
                <Badge variant="flat" color="secondary" size="sm" rounded="pill">
                  Now
                </Badge>
              ) : null}
            </li>
          );
        })}
        {user.status === "erased" ? (
          <li className="flex items-center gap-3 rounded-lg border border-secondary bg-secondary-lighter/30 px-3 py-2.5 dark:bg-secondary-lighter/10">
            <span className="size-2.5 shrink-0 rounded-full bg-secondary" />
            <div className="min-w-0 flex-1">
              <Text className="text-sm font-semibold text-gray-900">Erased</Text>
              <Text className="text-xs text-gray-500">No identity key held; cannot be reactivated</Text>
            </div>
          </li>
        ) : null}
      </ul>
      <div
        className={cn(
          "mt-4 flex items-start gap-3 rounded-lg border border-s-4 px-4 py-3",
          user.locked ? "border-muted border-s-red bg-red-lighter/30" : "border-muted border-s-green bg-green-lighter/20",
        )}
      >
        {user.locked ? <LockIcon aria-hidden className="mt-0.5 size-5 shrink-0 text-red-dark" /> : <UnlockIcon aria-hidden className="mt-0.5 size-5 shrink-0 text-green-dark" />}
        <div>
          <Text className="text-sm font-semibold text-gray-900">{user.locked ? "Sign-in locked" : "Sign-in not locked"}</Text>
          <Text className="text-xs text-gray-500">
            {user.locked
              ? user.lockedUntil
                ? `Unlocks by itself on ${formatDate(user.lockedUntil)}.`
                : "Stays locked until an administrator unlocks it."
              : "Reported by Identity beside the status, never folded into it."}
          </Text>
        </div>
      </div>
    </WidgetCard>
  );
}

function SecurityNotes() {
  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<InfoIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
          What each action does
        </CardTitle>
      }
      headerClassName="items-center"
    >
      <ul className="mt-5 flex flex-col gap-3 text-sm text-gray-600">
        <li className="flex gap-2.5">
          <RoleIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-secondary" />
          Changing the role revokes every session; they sign in again with the new role.
        </li>
        <li className="flex gap-2.5">
          <DeactivateIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-red-dark" />
          Deactivating signs the account out everywhere. Reactivating restores no session.
        </li>
        <li className="flex gap-2.5">
          <SessionIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-orange-dark" />
          Signing out everywhere leaves the account active — it only ends what is signed in now.
        </li>
      </ul>
    </WidgetCard>
  );
}

export type { UserSummary };
